import { useMutation, useQueryClient } from '@tanstack/react-query';

import { reportError } from 'helpers/error-logging';

import { PARTNER_STATE } from 'features/Sku/Items/SkuItems.constants';

import modifierGateway from './Modifiers.gateway';
import { modifierKeys } from './Modifiers.queryKeys';

const applySoldOut = (groups, { modifierId, soldOut }) =>
  (groups || []).map(group => ({
    ...group,
    modifiers: group.modifiers.map(modifier => (modifier.core.id === modifierId ? { ...modifier, soldOut } : modifier)),
  }));

const applyGroupEnabled = (groups, { groupId, enabled }) =>
  (groups || []).map(group => (group.core.id === groupId ? { ...group, enabled } : group));

export default function useModifierMutations({ venueId }) {
  const queryClient = useQueryClient();
  const groupsKey = modifierKeys.groups(venueId);
  // Deliberately not returning the promise: React Query awaits whatever onSettled returns before
  // it settles mutateAsync, so a failed save would spin until the refetch and its retries finished.
  const invalidateGroups = () => {
    queryClient.invalidateQueries({ queryKey: groupsKey });
  };

  const createGroup = useMutation({
    mutationFn: group => modifierGateway.createModifierGroup(venueId, group),
    onError: error => reportError('Error creating SKU modifier group', error),
    onSettled: invalidateGroups,
  });

  const setModifierSoldOut = useMutation({
    mutationFn: ({ modifierId, soldOut }) => modifierGateway.updateModifier(venueId, modifierId, { soldOut }),
    onMutate: async variables => {
      await queryClient.cancelQueries({ queryKey: groupsKey });
      const snapshot = queryClient.getQueryData(groupsKey);
      queryClient.setQueryData(groupsKey, current => applySoldOut(current, variables));
      return { snapshot };
    },
    onError: (error, variables, context) => {
      if (context) queryClient.setQueryData(groupsKey, context.snapshot);
      reportError('Error updating SKU modifier availability', error);
    },
    onSettled: invalidateGroups,
  });

  const updateGroup = useMutation({
    mutationFn: ({ groupId, group }) => modifierGateway.updateModifierGroup(venueId, groupId, group),
    onError: error => reportError('Error updating SKU modifier group', error),
    onSettled: invalidateGroups,
  });

  // Optimistic like setModifierSoldOut: the library row is a controlled dropdown, so the cache
  // must flip immediately or React snaps it back before the refetch lands.
  const setGroupEnabled = useMutation({
    mutationFn: ({ groupId, enabled }) => modifierGateway.updateModifierGroup(venueId, groupId, { enabled }),
    onMutate: async variables => {
      await queryClient.cancelQueries({ queryKey: groupsKey });
      const snapshot = queryClient.getQueryData(groupsKey);
      queryClient.setQueryData(groupsKey, current => applyGroupEnabled(current, variables));
      return { snapshot };
    },
    onError: (error, variables, context) => {
      if (context) queryClient.setQueryData(groupsKey, context.snapshot);
      reportError('Error updating SKU modifier group availability', error);
    },
    onSettled: invalidateGroups,
  });

  const linkGroupToItems = useMutation({
    mutationFn: ({ groupId, itemIds }) => modifierGateway.linkModifierGroupToItems(venueId, groupId, itemIds),
    onError: error => reportError('Error linking SKU modifier group to items', error),
    onSettled: invalidateGroups,
  });

  // No invalidate-on-settled, unlike the mutations above: the editor refreshes once at the end
  // rather than refetching the library between every option.
  const createModifier = useMutation({
    mutationFn: ({ groupId, modifier }) => modifierGateway.createModifier(venueId, groupId, modifier),
    onError: error => reportError('Error creating SKU modifier', error),
  });

  const updateModifier = useMutation({
    mutationFn: ({ modifierId, updates }) => modifierGateway.updateModifier(venueId, modifierId, updates),
    onError: error => reportError('Error updating SKU modifier', error),
  });

  const removeModifier = useMutation({
    mutationFn: ({ modifierId }) =>
      modifierGateway.updateModifier(venueId, modifierId, { partnerState: PARTNER_STATE.DELETED }),
    onError: error => reportError('Error removing SKU modifier', error),
  });

  const unlinkGroupFromItem = useMutation({
    mutationFn: ({ groupId, itemId }) => modifierGateway.unlinkModifierGroupFromItem(venueId, groupId, itemId),
    onError: error => reportError('Error unlinking SKU modifier group from item', error),
    onSettled: invalidateGroups,
  });

  return {
    createGroup,
    createModifier,
    linkGroupToItems,
    refreshGroups: invalidateGroups,
    removeModifier,
    setGroupEnabled,
    setModifierSoldOut,
    unlinkGroupFromItem,
    updateGroup,
    updateModifier,
  };
}
